import { useDeferredValue, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBriefcase } from '@fortawesome/pro-regular-svg-icons';
import { TextField, SelectOption, Pagination, Container, ModularTable, TableColorThemeEnum, CellBorderWidthEnum, SortEnum } from '@tryhackme/thm-ui-components';
import { StyledPagination, StyledSearch, StyledSelect, StyledFilters, StyledDownloadWrapper, Wrapper, StyledText, HeadBar, StyledDropDown, StyledSize, StyledHeading, StyledIcon, StyledNoRecordMessage, StyledHeadingIcon, StyledTextField } from './user-occupation.styles';
import { SelectOptions } from './user-occupation.constants';
import { formatData, sortData } from './user-occupation.helpers';
import { UserOccupationTableType, UserOccupationParams, UserOccupationProps } from './user-occupation.types';
import { UserOccupationDownload } from './components/user-occupation-download';
import { useGetUserOccupationQuery } from './user-occupation.slice';
export const UserOccupation = ({
  title,
  columns
}: UserOccupationProps) => {
  const [occupationParams, setOccupationParams] = useState<UserOccupationParams>({
    page: 1,
    limit: 10,
    searchText: '',
    sortOrderText: '',
    sortColumn: ''
  });
  const deferredOccupationParams = useDeferredValue(occupationParams);
  const [searchValue, setSearchValue] = useState('');
  const [activeSortColumn, setActiveSortColumn] = useState<UserOccupationTableType['activeSortColumn']>(null);
  const [selectedOption, setSelectedOption] = useState<SelectOption>({
    id: '1',
    value: '10',
    label: '10'
  });
  const {
    data,
    isLoading
  } = useGetUserOccupationQuery(deferredOccupationParams);
  const numberOfRecords = {
    total: data?.data?.totalDocs ?? 0,
    fetched: data?.data?.docs?.length ?? 0
  };
  const defaultOption: SelectOption = {
    id: '1',
    value: '10',
    label: '10'
  };
  const formattedRows: UserOccupationTableType['rows'] = formatData(data?.data?.docs ?? []);
  const formattedSortableColumn = columns.map(col => {
    const obj = {
      ...col
    };
    if (obj.sortFunction) {
      obj.sortFunction = () => sortData(setActiveSortColumn, setOccupationParams, obj.key);
    }
    return obj;
  });
  const handleSelection = (option: SelectOption = defaultOption) => {
    setSelectedOption(() => ({
      value: option.value,
      label: option.label,
      id: option.id
    }));
    setOccupationParams(prevParams => ({
      ...prevParams,
      limit: Number.parseInt(option.value, 10),
      page: 1
    }));
  };
  const handlePageChange = (pageNumber: number) => {
    setOccupationParams(prevParams => ({
      ...prevParams,
      page: pageNumber
    }));
  };
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOccupationParams(prevParams => ({
      ...prevParams,
      searchText: e.target.value,
      page: 1
    }));
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    handleTextChange(e);
  };
  return <section data-sentry-component="UserOccupation" data-sentry-source-file="user-occupation.tsx">
      <Container data-sentry-element="Container" data-sentry-source-file="user-occupation.tsx">
        <HeadBar data-sentry-element="HeadBar" data-sentry-source-file="user-occupation.tsx">
          <StyledIcon data-sentry-element="StyledIcon" data-sentry-source-file="user-occupation.tsx">
            <StyledHeadingIcon icon={faBriefcase} data-sentry-element="StyledHeadingIcon" data-sentry-source-file="user-occupation.tsx" />
          </StyledIcon>
          <StyledHeading data-sentry-element="StyledHeading" data-sentry-source-file="user-occupation.tsx">{title}</StyledHeading>
        </HeadBar>
        <StyledFilters data-sentry-element="StyledFilters" data-sentry-source-file="user-occupation.tsx">
          <StyledSearch data-sentry-element="StyledSearch" data-sentry-source-file="user-occupation.tsx">
            <StyledTextField placeholder="Enter keywords" variant="input" leftAdornment={<FontAwesomeIcon icon={faSearch} />} onChange={handleInputChange} value={searchValue} data-sentry-element="StyledTextField" data-sentry-source-file="user-occupation.tsx" />
          </StyledSearch>
          <StyledDropDown data-sentry-element="StyledDropDown" data-sentry-source-file="user-occupation.tsx">
            <StyledDownloadWrapper data-sentry-element="StyledDownloadWrapper" data-sentry-source-file="user-occupation.tsx">
              <UserOccupationDownload occupationData={formattedRows ?? []} data-sentry-element="UserOccupationDownload" data-sentry-source-file="user-occupation.tsx" />
            </StyledDownloadWrapper>
            <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="user-occupation.tsx">Show</StyledSize>
            <StyledSelect data-sentry-element="StyledSelect" data-sentry-source-file="user-occupation.tsx">
              <TextField isMulti={false} isSearchable={false} variant="select" handleSelection={handleSelection} options={SelectOptions} selected={selectedOption} defaultValue={SelectOptions[0]} data-sentry-element="TextField" data-sentry-source-file="user-occupation.tsx" />
            </StyledSelect>
            <StyledSize data-sentry-element="StyledSize" data-sentry-source-file="user-occupation.tsx">records</StyledSize>
          </StyledDropDown>
        </StyledFilters>
        <ModularTable isLoading={isLoading} activeSortColumn={activeSortColumn} activeSortOrder={occupationParams.sortOrderText === 'descending' ? SortEnum.descending : SortEnum.ascending} columns={formattedSortableColumn} rows={formattedRows} colorTheme={TableColorThemeEnum.grayWhite} cellBorderWidth={CellBorderWidthEnum.thick} data-sentry-element="ModularTable" data-sentry-source-file="user-occupation.tsx" />
        {numberOfRecords.fetched === 0 && occupationParams.searchText !== '' && <StyledNoRecordMessage>No Occupation found</StyledNoRecordMessage>}
        <Wrapper data-sentry-element="Wrapper" data-sentry-source-file="user-occupation.tsx">
          <StyledText data-sentry-element="StyledText" data-sentry-source-file="user-occupation.tsx">
            <p>
              Showing{' '}
              {numberOfRecords.fetched > 0 ? `${(occupationParams.page - 1) * occupationParams.limit + 1} to ` : 0}
              {numberOfRecords.fetched > 0 ? `${occupationParams.page * occupationParams.limit} ` : ` `}
              of {numberOfRecords.total} records
            </p>
          </StyledText>
          {numberOfRecords.fetched > 0 && <StyledPagination>
              <Pagination onChange={handlePageChange} selectedPage={occupationParams.page} totalPages={Math.ceil(numberOfRecords.total / occupationParams.limit)} />
            </StyledPagination>}
        </Wrapper>
      </Container>
    </section>;
};